import React from 'react';
import {
  Checkbox as MuiCheckbox,
  getCheckboxUtilityClass,
  useThemeProps,
} from '@mui/material';
import clsx from 'clsx';
import { checkboxClasses, INDETERMINATE_PADDING, TICK_SIZES } from './constants';
import {
  CheckboxProps, CheckBoxSize, CheckboxTickRenderValues,
} from './types';

function CheckboxContainer(props: React.PropsWithChildren) {
  return (
    <div
      className={checkboxClasses.container}
      {...props}
    />
  );
}

function CheckboxTick({
  ax, ay, bx, by, rx, ry, containerSize,
}: CheckboxTickRenderValues) {
  return (
    <svg
      width={containerSize}
      height={containerSize}
      viewBox={`0 0 ${containerSize} ${containerSize}`}
      className={checkboxClasses.tick}
    >
      <polyline points={`${ax},${ay} ${rx},${ry} ${bx},${by}`} />
    </svg>
  );
}

function CheckboxIndeterminateTick({ containerSize }: Pick<CheckboxTickRenderValues, 'containerSize'>) {
  const svgViewportSize = containerSize - 2;
  const verticalMiddle = svgViewportSize / 2;
  const ax = INDETERMINATE_PADDING;
  const bx = svgViewportSize - INDETERMINATE_PADDING;

  return (
    <svg
      width={svgViewportSize}
      height={svgViewportSize}
      viewBox={`0 0 ${svgViewportSize} ${svgViewportSize}`}
      className={checkboxClasses.tick}
    >
      <polyline points={`${ax},${verticalMiddle} ${bx},${verticalMiddle}`} />
    </svg>
  );
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  (inProps: CheckboxProps, ref: React.RefObject<HTMLButtonElement>) => {
    const props: CheckboxProps = useThemeProps({
      props: {
        size: 'medium',
        ...inProps,
      },
      name: 'MuiCheckbox',
    });
    const { size = 'medium', className, ...rest } = props;
    const precalculatedTickValues = TICK_SIZES[size as CheckBoxSize];

    return (
      <MuiCheckbox
        ref={ref}
        icon={<CheckboxContainer />}
        checkedIcon={(
          <CheckboxContainer>
            <CheckboxTick {...precalculatedTickValues} />
          </CheckboxContainer>
        )}
        indeterminateIcon={(
          <CheckboxContainer>
            <CheckboxIndeterminateTick {...precalculatedTickValues} />
          </CheckboxContainer>
        )}
        className={clsx(
          className,
          getCheckboxUtilityClass(size),
        )}
        size={size}
        {...rest}
      />
    );
  },
);

Checkbox.displayName = 'Checkbox';
